import Oidc from 'oidc-client';

// @const {String} projectClientId 專案的clientId
const projectClientId = process.env.VUE_OIDC_CLIENT_ID || 'cm-author-tool-web';

// @const {String} identityDomain 登入網域
// @const {String} identityPath 登入目錄
// @const {String} productionDomain 產品網域
// @const {String} productionPath 產品目錄
const identityDomain = process.env.VUE_APP_IDENTITY_DOMAIN || 'https://auth.cmoney.tw';
const identityPath = '/identity';
const productionDomain = process.env.VUE_APP_PRODUCTION_DOMAIN || 'https://www.cmoney.tw';
const productionPath = process.env.VUE_APP_PRODUCTION_PATH;

// @const {Object} config oidc-client 套件使用設定
const managerConfig = {
  userStore: new Oidc.WebStorageStateStore(),
  authority: identityDomain,
  client_id: projectClientId,
  redirect_uri: `${productionDomain}${productionPath}/login`,
  response_type: 'code',
  scope: 'openid nickname',
  post_logout_redirect_uri: `${productionDomain}${productionPath}/logout`,
  silent_redirect_uri: `${productionDomain}${productionPath}/refresh`,
  accessTokenExpiringNotificationTime: 10,
  automaticSilentRenew: true, // 使用 refresh_token
  filterProtocolClaims: false,
  loadUserInfo: false,
  monitorSession: false,
  metadata: {
    issuer: 'https://www.cmoney.tw',
    authorization_endpoint: `${identityDomain}${identityPath}/authorize`,
    token_endpoint: `${identityDomain}${identityPath}/token`,
    end_session_endpoint: `${identityDomain}${identityPath}/endsession`,
    jwks_uri: `${identityDomain}${identityPath}/keys/jwks`,
    check_session_iframe: `${identityDomain}${identityPath}/checksession`,
  },
};

class AuthManager {
  constructor() {
    this.PROMPT = 'select_account';
  }

  /** 初始化 Oidc manager
      */
  initManager(config) {
    Oidc.Log.logger = console;
    // 設定 => DEBUG 可以看記錄情況 , NONE 不印出任何 DEBUG 訊息
    Oidc.Log.level = Oidc.Log.NONE;
    this.manager = new Oidc.UserManager(config);
  }

  /** 處理刷新token錯誤
      * @param {function} errorCallback - 處理刷新 token error callback
      */
  handleSilentRenewError(errorCallback) {
    // 刷新失敗 則 刪除此裝置的使用者、查同裝置上其他平台是否有登入情況
    this.manager.removeUser();
    this.manager.signinSilent()
      .catch((error) => {
        console.log(error.message);
        if (errorCallback) errorCallback();
      });
  }

  /** Oidc.Manager的事件處理
      * @param {function} renewTokensErrorCallback - 處理刷新 token error callback
      * @param {function} renewTokens - 更新 tokens 的方法
      */
  setOidcManagerEvents(renewTokensErrorCallback, renewTokens) {
    // oidc-client 協助 refresh_token 事件處理 => 取得最新的 tokens
    this.manager.events.addUserLoaded(async (response) => {
      // @const {Object} userRenewTokens 使用者目前最新的tokens
      const userRenewTokens = {
        at: response.access_token,
        idt: response.id_token,
        rt: response.refresh_token,
      };
      if (renewTokens) renewTokens(userRenewTokens);
    });
    // oidc-client 登出事件處理 若同一裝置上有網站登出，所有網站都會一起登出
    this.manager.events.addUserSignedOut(() => {
      this.manager.removeUser().then(() => this.manager.signinSilent());
    });
    // oidc-client access_token 過期 則 先嘗試刷新 token
    this.manager.events.addAccessTokenExpired(() => {
      this.manager.signinSilent()
        .catch(() => {
          this.handleSilentRenewError(renewTokensErrorCallback);
        });
    });
    // oidc-client 刷新token錯誤事件處理
    this.manager.events.addSilentRenewError(() => {
      this.handleSilentRenewError(renewTokensErrorCallback);
    });
  }
}

export default {
  namespaced: true,
  state: {
    // @param {String} clientId client_id
    // @param {Object} tokens 驗證用的token
    clientId: projectClientId,
    tokens: {
      // @param {String} at access_token
      // @param {String} idt id_token
      // @param {String} rt refresh_token
      at: '',
      idt: '',
      rt: '',
    },
    // @param {Number} timeId token自動刷新產生的timeoutId default為-1
    timeoutId: -1,
    // @param {Object} manager oidc的管理
    manager: {},
    // @param {Boolean} isOidcInitialized 是否已初始化 oidc manager（避免重複註冊事件）
    isOidcInitialized: false,
  },
  actions: {
    // 更新token
    updateToken({ commit }, tokens) {
      commit('UPDATE_TOKEN', tokens);
    },
    // 設定timeoutId
    setTimeoutId({ commit }, timeoutId) {
      commit('SET_TIMEOUT_ID', timeoutId);
    },
    // 停止之前的刷新token的循環
    stopBeforeTimeout({ commit }) {
      commit('STOP_BEFORE_TIMEOUT');
    },
    // 初始化 Oidc 實體
    initialOidc({ state, dispatch, commit }) {
      // 已初始化則不重複建立，避免重複註冊 OIDC 事件處理
      if (state.isOidcInitialized) return;
      const authManager = new AuthManager();
      authManager.initManager(managerConfig);
      // 停止之前的刷新token，拿新的匿名tokens
      const renewTokensErrorCallback = () => {
        commit('STOP_BEFORE_TIMEOUT');
        dispatch('getAnonymousTokens');
      };
      const renewTokens = (userRenewTokens) => { dispatch('updateToken', userRenewTokens); };
      authManager.setOidcManagerEvents(renewTokensErrorCallback, renewTokens);
      commit('INITIAL_OIDC', authManager.manager);
    },
    // 清除不完整授權的請求
    clearIncompleteAuthorize({ getters }) {
      getters.getManager.clearStaleState();
    },
    // 登入轉導至統一登入頁面
    loginRedirectUnityPage({ getters }) {
      // 先刪除使用者紀錄，防止有重複使用者紀錄的可能
      getters.getManager.removeUser();
      getters.getManager.signinRedirect({ prompt: 'select_account' });
    },
    // 登入後授權回來換使用者的token
    loginRedirectCallback({ getters }) {
      return getters.getManager.signinRedirectCallback();
    },
    // 登入後授權回來換使用者的token
    idpRefreshCallback({ getters }) {
      return getters.getManager.signinSilentCallback();
    },
    // 登出轉導至統一登入頁面
    async logoutRedirectUnityPage({ getters }) {
      const manager = getters.getManager;
      const user = await manager.getUser();
      const idToken = (user && user.id_token) || getters.getTokens.idt;
      manager.signoutRedirect(idToken ? { id_token_hint: idToken } : undefined);
    },
    // 刪除local端登入的使用者
    removeLocalUser({ getters }) {
      getters.getManager.removeUser();
    },
    // 從 OIDC userStore 取出使用者並灌回 Vuex tokens（整頁重整時補上記憶體狀態）
    async hydrateFromOidc({ getters, dispatch }) {
      // 已有 token 則不重複處理
      if (getters.getTokens.at) return;
      const user = await getters.getManager.getUser();
      if (user && !user.expired) {
        dispatch('updateToken', {
          at: user.access_token,
          idt: user.id_token,
          rt: user.refresh_token,
        });
      }
    },
    // 確認IDP是否為登入狀態
    async checkIDPIsLogin({ getters }) {
      const manager = getters.getManager;
      manager.signinSilent()
        .catch((error) => {
          console.log(error.message);
        });
    },
    // ---- 抽象 ----
    // interface 取得匿名tokens
    async getAnonymousTokens() {
      throw new Error('Actions getAnonymousTokens need implement.');
    },
  },
  mutations: {
    // 更新token
    UPDATE_TOKEN(state, tokens) {
      state.tokens = tokens;
    },
    // 設定timeoutId
    SET_TIMEOUT_ID(state, timeoutId) {
      state.timeoutId = timeoutId;
    },
    // 停止之前的刷新token的循環
    STOP_BEFORE_TIMEOUT(state) {
      clearTimeout(state.timeoutId);
    },
    // init oidc-manager
    INITIAL_OIDC(state, manager) {
      state.manager = manager;
      state.isOidcInitialized = true;
    },
  },
  getters: {
    getClientId(state) {
      return state.clientId;
    },
    // 取得token
    getTokens(state) {
      return state.tokens;
    },
    // 取得manager
    getManager(state) {
      return state.manager;
    },
    // 取得使用者
    getUserTokens(state) {
      return state.manager.getUser();
    },
  },
};
